
import React, { useState, useMemo, useEffect } from 'react'
import { withComponentContext } from 'widgets-shared/core'
import { withTranslations } from 'widgets-shared/core'
import { Grid } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery';
import SwipeableViews from 'react-swipeable-views'
import FixtureHeader from 'smartstream-widgets/components/FixtureHeader/FixtureHeader'
import TabBar from 'smartstream-widgets/controls/TabBar/TabBar'
import OddsComparisonPanel from './OddsComparisonPanel'
import { buildMatchState, getMarketsLabels } from './FixtureCardUtils'
import { compose } from 'widgets-shared/core'
import { marketsTranslations } from 'smartstream-widgets/constants/markets'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { Props } from './types'
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { alpha  } from '@mui/material/styles'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme';

const styles = (theme: Theme) =>  createStyles({
  root: {},
  header: {
    backgroundColor: theme.palette.background.default
  },
  marketOddLines: {
    textAlign: 'center',
    padding: `20px 15px`,
    '&:nth-child(odd)': {
      background: theme.palette.grey[50],
      border: `1px solid ${theme.palette.grey[300]}`,
      borderWidth: `0.5px 0px`
    }
  },
  marketOddLinesDraw: {
    padding: `25px 8px 8px`,
    '&:nth-child(odd)': {
      background: theme.palette.grey[50],
      border: `1px solid ${theme.palette.grey[300]}`,
      borderWidth: `0.5px 0px`
    }
  },
  marketOddLinesDrawLink: {
    padding: `25px 8px 8px`,
    width: '100%',
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplate:`
        "icon odds" auto
        "txt1 txt2" min-content / min-content 1fr`
  },
  tabBarContainer: {
    boxShadow: 'none',
      borderBottom: `1px solid ${theme.palette.grey[500]}`
  },
  tabBar: {
    [theme.breakpoints.up('cs')]: {
      fontSize: 11
    },
    fontSize: 9,
    textTransform: 'uppercase'
  },
  teamName: {
    [theme.breakpoints.up('cs')]: {
      fontSize: 16
    },
    fontSize: 14,
    lineHeight: 1
  },
  oddButton: {
    '& p': {
      fontSize: 12
    },
    '& $captionOdd:nth-last-child(3)': {
      fontSize: 11
    },
    '& $captionOdd': {
      fontSize: 12
    }
  },
  oddButtonWithDraw: {
    [theme.breakpoints.up('cs')]: {
      minWidth: 96
    },
    '& p': {
      fontSize: 12
    }
  },
  scoreContainer: {
    [theme.breakpoints.up('cs')]: {
      marginTop: 20,
      zIndex: 1
    }
  },
  captionOdd: {},
  affiliateText: {
    fontSize: 11,
      marginTop: 5
  },
  bestSelection: {
    backgroundColor: alpha(theme.palette.success.main, 0.11),
      border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
      color: theme.palette.success.main,
      '&:hover': {
      background: theme.palette.success.main,
        '& $captionOdd': {
        color: theme.palette.common.white
      }
    },
    '&$buttonDisabled': {
      '& $captionOdd': {
        color: theme.palette.success.main
      }
    }
  },
  bookmakerIcon: {
    [theme.breakpoints.up('cs')]: {
      width: 32
    }
  },
  buttonDisabled: {},
  showMoreText: {
    color: theme.palette.primary.main,
      fontSize: 13
  },
  showMoreButton: {
    padding: 20
  },
  liveScore: {
    [theme.breakpoints.up('cs')]: {
      fontSize: 24
    }
  },
  iconContainer: {
    [theme.breakpoints.up('cs')]: {
      width: 150
    },
    justifyContent: 'center',
    width: 62
  },
  offerText: {
    [theme.breakpoints.up('cs')]: {
      gridArea: 'txt1',
      textAlign: 'center'
    }
  },
  rootDraw: {
    gridTemplateColumns: 'min.content 1fr'
  },
  marketOddsContainerDraw: {
    justifyContent: 'space-around'
  },
  logoFlagSize: {}
})


export function FixtureCard(props: Props) {
  const {
    bookmakers = {},
    isBookmakerLinkEnabled,
    classes,
    config: {
      initialMarketType = ''
    },
    fixture: {
      competitionId,
      competitors,
      fixtureId,
      markets,
      matchSummaries,
      scheduledStartTime
    } = {},
    isAwayTeamFirst = false,
    oddsFormat,
    theme,
    translations,
  } = props

  const logoFlagOverrides = {
    flagHeight: 52,
    flagWidth: 130,
    logoSize: 42
  }
  const matchState = useMemo(() => buildMatchState(matchSummaries), [matchSummaries])
  const marketLabels = getMarketsLabels(markets, initialMarketType, translations)
  const [tabIndex, setValue] = useState(0)

  useEffect(() => {
    if ((tabIndex + 1) > marketLabels.length) {
      setValue(0)
    }
  }, [marketLabels])

  return (
    <Grid classes={{ root: classes.root }} data-testid="fixture-card-container">
      <FixtureHeader
        logoFlagOverrides={useMediaQuery(theme.breakpoints.up('cs')) ? logoFlagOverrides : {}}
        competitors={competitors}
        scheduledStartTime={scheduledStartTime}
        matchState={matchState}
        isAwayTeamFirst={isAwayTeamFirst}
        classes={{
          liveScore: classes.liveScore,
          root: classes.header,
          teamName: classes.teamName,
          scoreContainer: classes.scoreContainer,
          logoFlagSize: classes.logoFlagSize
        }}
      />
      <TabBar
        value={tabIndex}
        appBarClasses={{
          root: classes.tabBarContainer
        }}
        tabElements={marketLabels}
        onTabChange={setValue}
        tabClasses={{
          root: classes.tabBar
        }}
      />
      <SwipeableViews
        axis={'x'}
        index={tabIndex}
        onChangeIndex={setValue}
        key={fixtureId}
      >
        {
          marketLabels.map(({marketKey}, index) => (
            <OddsComparisonPanel
              bookmakers={bookmakers}
              isBookmakerLinkEnabled={isBookmakerLinkEnabled}
              classes={classes}
              competitionId={competitionId}
              competitors={competitors}
              fixtureId={fixtureId}
              index={index}
              id={marketKey}
              isAwayTeamFirst={isAwayTeamFirst}
              key={marketKey}
              markets={markets[marketKey]}
              oddsFormat={oddsFormat}
              tabIndex={tabIndex}
              translations={translations}
            />
          ))
        }
      </SwipeableViews>
    </Grid>
  )
}

export default compose(
  withComponentContext,
  withTranslations((t: TranslationFunction) => {
    return {
      ...marketsTranslations(t),
      showMore: t('showMore.translation', {
        defaultValue: 'Show more odds',
        context: 'Text to indicate to show more odds if clicked, inside Odds Comparison Grid widget'
      }),
      showLess: t('showLess.translation', {
        defaultValue: 'Show less odds',
        context: 'Text to indicate to show less odds if clicked, inside Odds Comparison Grid widget'
      })
    }
  }),
  withStyles(styles, { name: 'FixtureCard', withTheme: true, defaultTheme: getDefaultTheme })
)(FixtureCard)
